import React, { memo, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import type { PaperProps } from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import type { Control, UseFormGetValues } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import dayjs, { type Dayjs } from 'dayjs';
import Draggable from 'react-draggable';

import type { SchedulingRule } from 'repository/schedule/@types/SchedulingRuleModel';
import { ScheduleService } from 'services/ScheduleService';
import type { SchedulingRuleAssign } from 'repository/schedule/@types/SchedulingRuleAssing';

type Props = {
    open: boolean;
    setOpen: (v: boolean) => void;
    data: Employee;
    cleanSelected: () => void;
    reloading: () => void;
};

type Employee = {
    id: number;
    [key: string]: any;
};

type ScheduleRuleRow = {
    id: number;
    employee_schedule_rule_id: number | null;
    employee_id: number;
    scheduling_rule_id: number | '';
    start_date: Dayjs;
    end_date: Dayjs;
};

type FormValues = {
    schedule_rules: ScheduleRuleRow[];
};

type ScheduleRuleRowWithIndex = ScheduleRuleRow & { _index: number };

const INDEFINITE_DATE = dayjs('3000-01-01');
const scheduleService = new ScheduleService();

const getSchedulingRuleId = (rule: SchedulingRule): number => rule.ruleset_id ?? rule.scheduling_rule_id ?? rule.id ?? 0;
const getAssignmentId = (assignment: SchedulingRuleAssign): number | null => assignment.ruleset_r_employee_id ?? assignment.employee_schedule_rule_id ?? assignment.id ?? null;

type CellSharedProps = {
    rowIndex: number;
    control: Control<FormValues>;
    loading: boolean;
    getValues: UseFormGetValues<FormValues>;
    validateRow: (row: ScheduleRuleRow, index: number) => string | null;
    t: TFunction;
};

type RuleSelectCellProps = CellSharedProps & { rules: SchedulingRule[] };

const RuleSelectCell = memo<RuleSelectCellProps>(({ rowIndex, control, loading, rules, validateRow, getValues, t }) => (
    <Controller
        name={`schedule_rules.${rowIndex}.scheduling_rule_id`}
        control={control}
        rules={{
            validate: (_, formValues) => {
                return validateRow(formValues.schedule_rules[rowIndex], rowIndex) ?? true;
            },
        }}
        render={({ field, fieldState }) => {
            const validationError = validateRow(getValues(`schedule_rules.${rowIndex}`), rowIndex);
            return (
                <FormControl fullWidth size="small" error={!!fieldState.error || !!validationError}>
                    <InputLabel id={`sr-label-${rowIndex}`}>{t('timekeeper.employees.dialog.assign_schedule_rule.rule_label')}</InputLabel>
                    <Select {...field} labelId={`sr-label-${rowIndex}`} label={t('timekeeper.employees.dialog.assign_schedule_rule.rule_label')} disabled={loading}>
                        {rules.map((rule) => (
                            <MenuItem key={getSchedulingRuleId(rule)} value={getSchedulingRuleId(rule)}>
                                {rule.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {validationError && <FormHelperText>{validationError}</FormHelperText>}
                </FormControl>
            );
        }}
    />
));

type DateCellProps = CellSharedProps & { fieldName: 'start_date' | 'end_date' };

const DateCell = memo<DateCellProps>(({ rowIndex, fieldName, control, loading, validateRow, getValues, t }) => {
    const isEndDate = fieldName === 'end_date';
    const labelKey = isEndDate ? 'timekeeper.employees.dialog.assign_schedule_rule.end_date_label' : 'timekeeper.employees.dialog.assign_schedule_rule.start_date_label';
    const requiredKey = isEndDate ? 'timekeeper.employees.dialog.assign_schedule_rule.end_date_required' : 'timekeeper.employees.dialog.assign_schedule_rule.start_date_required';

    return (
        <Controller
            name={`schedule_rules.${rowIndex}.${fieldName}`}
            control={control}
            rules={{
                required: t(requiredKey),
                validate: (_, formValues) => {
                    return validateRow(formValues.schedule_rules[rowIndex], rowIndex) ?? true;
                },
            }}
            render={({ field, fieldState }) => {
                const validationError = validateRow(getValues(`schedule_rules.${rowIndex}`), rowIndex);
                return (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <DatePicker
                            value={field.value}
                            onChange={field.onChange}
                            inputRef={field.ref}
                            sx={{ mt: 0.5 }}
                            label={t(labelKey)}
                            disabled={loading}
                            {...(isEndDate && {
                                localeText: {
                                    okButtonLabel: t('timekeeper.employees.dialog.assign_schedule_rule.set_indefinite'),
                                },
                            })}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    error: !!fieldState.error || !!validationError,
                                    helperText: fieldState.error?.message || validationError || '',
                                },
                                ...(isEndDate && {
                                    actionBar: {
                                        actions: ['accept' as const],
                                        onAccept: () => field.onChange(INDEFINITE_DATE),
                                    },
                                }),
                            }}
                        />
                    </LocalizationProvider>
                );
            }}
        />
    );
});

const PaperComponent = (props: PaperProps) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable nodeRef={nodeRef as RefObject<HTMLDivElement>} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
};

export const AssignScheduleRule: React.FC<Props> = ({ open, setOpen, data, cleanSelected, reloading }) => {
    const { t } = useTranslation();
    const employeeId = data?.id;

    const [alerts, setAlerts] = useState(false);
    const [loadingAssignments, setLoadingAssignments] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingRules, setLoadingRules] = useState(false);
    const [availableRules, setAvailableRules] = useState<SchedulingRule[]>([]);
    const idCounterRef = useRef(0);

    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
        getValues,
    } = useForm<FormValues>({
        defaultValues: { schedule_rules: [] },
        mode: 'onChange',
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'schedule_rules',
    });

    const rows = useMemo<ScheduleRuleRowWithIndex[]>(() => fields.map((field, index) => ({ ...field, _index: index })), [fields]);

    const loading = loadingRules || loadingAssignments || saving;

    const buildNewRow = useCallback((): ScheduleRuleRow => {
        idCounterRef.current += 1;
        return {
            id: idCounterRef.current,
            employee_schedule_rule_id: null,
            employee_id: employeeId,
            scheduling_rule_id: '',
            start_date: dayjs(),
            end_date: dayjs(),
        };
    }, [employeeId]);

    const closeForm = useCallback(() => {
        reset({ schedule_rules: [] });
        setAlerts(false);
        cleanSelected();
        setOpen(false);
    }, [cleanSelected, reset, setOpen]);

    const handleClose = useCallback(() => {
        if (isDirty) setAlerts(true);
        else closeForm();
    }, [closeForm, isDirty]);

    const initRows = useCallback(
        (backendRows: ScheduleRuleRow[]) => {
            if (backendRows.length === 0) {
                idCounterRef.current = 0;
                reset({ schedule_rules: [buildNewRow()] });
                return;
            }
            idCounterRef.current = backendRows.length;
            reset({ schedule_rules: backendRows });
        },
        [buildNewRow, reset],
    );

    const validateRow = useCallback(
        (row: ScheduleRuleRow, index: number): string | null => {
            if (!row.scheduling_rule_id) {
                return t('timekeeper.employees.dialog.assign_schedule_rule.select_required');
            }
            if (!row.start_date) {
                return t('timekeeper.employees.dialog.assign_schedule_rule.start_date_required');
            }
            if (!row.end_date) {
                return t('timekeeper.employees.dialog.assign_schedule_rule.end_date_required');
            }

            const startDate = row.start_date;
            const endDate = row.end_date;
            const all = getValues('schedule_rules');

            if (startDate.isAfter(endDate) && !endDate.isSame(INDEFINITE_DATE, 'day')) {
                return t('timekeeper.employees.dialog.assign_schedule_rule.date_range_invalid');
            }

            const hasIndefinite = all.some((r, idx) => idx !== index && r.end_date?.year() >= 3000);
            if (hasIndefinite && endDate.isSame(INDEFINITE_DATE, 'day')) {
                return t('timekeeper.employees.dialog.assign_schedule_rule.indefinite_exists');
            }

            const overlappingIdx = all.findIndex((r, idx) => {
                if (idx === index || !r.start_date || !r.end_date) return false;
                return startDate.isBefore(r.end_date) && endDate.isAfter(r.start_date);
            });

            if (overlappingIdx !== -1) {
                const overlapping = all[overlappingIdx];
                const ruleName = availableRules.find((rule) => getSchedulingRuleId(rule) === overlapping.scheduling_rule_id)?.name;

                return `${t('timekeeper.employees.dialog.assign_schedule_rule.date_overlap')}: "${ruleName}" (${overlapping.start_date?.format('DD/MM/YYYY')} - ${overlapping.end_date?.format('DD/MM/YYYY')})`;
            }

            return null;
        },
        [availableRules, getValues, t],
    );

    const addRow = useCallback(() => {
        append(buildNewRow());
    }, [append, buildNewRow]);

    const removeRow = useCallback(
        (index: number) => {
            remove(index);
        },
        [remove],
    );

    const onSubmit = useCallback(
        async (formData: FormValues) => {
            const hasErrors = formData.schedule_rules.some((row, i) => validateRow(row, i) !== null);
            if (hasErrors) {
                enqueueSnackbar(t('timekeeper.employees.dialog.assign_schedule_rule.alerts.validation_error'), { variant: 'error' });
                return;
            }

            const payload: SchedulingRuleAssign[] = formData.schedule_rules.map((row) => ({
                employee_id: employeeId,
                ruleset_id: row.scheduling_rule_id as number,
                start_date: row.start_date.format('YYYY-MM-DD'),
                end_date: row.end_date.format('YYYY-MM-DD'),
            }));

            try {
                setSaving(true);
                const existingAssignments = await scheduleService.scheduling_rules_assign.getAll({ employee_id: employeeId });

                if (existingAssignments.length === 0) {
                    await scheduleService.scheduling_rules_assign.create(payload);
                } else {
                    const updateRows = formData.schedule_rules.filter((row) => row.employee_schedule_rule_id !== null);

                    if (updateRows.length > 0) {
                        await Promise.all(
                            updateRows.map((row) => {
                                const rulesetREmployeeId = row.employee_schedule_rule_id as number;
                                return scheduleService.scheduling_rules_assign.delete(rulesetREmployeeId);
                            }),
                        );
                    }
                }

                enqueueSnackbar(t('timekeeper.employees.dialog.assign_schedule_rule.alerts.success'), { variant: 'success' });
                reloading();
                closeForm();
            } catch (error) {
                console.error('Error saving schedule rules:', error);
                enqueueSnackbar(t('timekeeper.employees.dialog.assign_schedule_rule.alerts.error'), { variant: 'error' });
            } finally {
                setSaving(false);
            }
        },
        [closeForm, employeeId, reloading, t, validateRow],
    );

    useEffect(() => {
        if (!open || !employeeId) return;

        let mounted = true;

        const load = async () => {
            setLoadingAssignments(true);
            setLoadingRules(true);
            try {
                const rules = await scheduleService.scheduling_rules.getAll();
                if (mounted) {
                    setAvailableRules(rules);
                }
                const existingAssignments = await scheduleService.scheduling_rules_assign.getAll({ employee_id: employeeId });

                if (!mounted) return;

                const mappedRows: ScheduleRuleRow[] = existingAssignments.map((assignment, index) => ({
                    id: index + 1,
                    employee_schedule_rule_id: getAssignmentId(assignment),
                    employee_id: assignment.employee_id,
                    scheduling_rule_id: assignment.ruleset_id ?? assignment.scheduling_rule_id ?? '',
                    start_date: dayjs(assignment.start_date),
                    end_date: dayjs(assignment.end_date),
                }));

                initRows(mappedRows);
            } catch (error) {
                console.error('Error loading schedule rules assignments:', error);
                if (mounted) {
                    enqueueSnackbar(t('timekeeper.employees.dialog.assign_schedule_rule.alerts.load_error'), { variant: 'error' });
                    initRows([]);
                    setAvailableRules([]);
                }
            } finally {
                if (mounted) {
                    setLoadingAssignments(false);
                    setLoadingRules(false);
                }
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, [employeeId, initRows, open, t]);

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'actions',
                type: 'actions',
                headerName: t('timekeeper.employees.dialog.assign_schedule_rule.actions_label'),
                flex: 0.5,
                headerAlign: 'center',
                align: 'center',
                getActions: (params: any) => {
                    const rowIndex: number = params.row._index;
                    const isLastRow = rowIndex === rows.length - 1;
                    const isFirstRow = rowIndex === 0;

                    const actions: React.ReactElement[] = [];

                    if (!(isFirstRow && isLastRow)) {
                        actions.push(
                            <GridActionsCellItem
                                key="delete"
                                icon={<DeleteIcon color="error" />}
                                label={t('general.buttons.delete')}
                                onClick={() => removeRow(rowIndex)}
                                disabled={loading}
                                showInMenu={false}
                            />,
                        );
                    }

                    if (isLastRow) {
                        actions.push(<GridActionsCellItem key="add" icon={<AddIcon color="action" />} label={t('general.buttons.add')} onClick={addRow} disabled={loading} showInMenu={false} />);
                    }

                    return actions;
                },
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'scheduling_rule_id',
                headerName: t('timekeeper.employees.dialog.assign_schedule_rule.rule_label'),
                flex: 1.5,
                align: 'center',
                renderCell: (params: any) => (
                    <RuleSelectCell rowIndex={params.row._index} control={control} loading={loading} rules={availableRules} validateRow={validateRow} getValues={getValues} t={t} />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'start_date',
                headerName: t('timekeeper.employees.dialog.assign_schedule_rule.start_date_label'),
                flex: 1,
                align: 'center',
                renderCell: (params: any) => <DateCell rowIndex={params.row._index} fieldName="start_date" control={control} loading={loading} validateRow={validateRow} getValues={getValues} t={t} />,
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'end_date',
                headerName: t('timekeeper.employees.dialog.assign_schedule_rule.end_date_label'),
                flex: 1,
                align: 'center',
                renderCell: (params: any) => <DateCell rowIndex={params.row._index} fieldName="end_date" control={control} loading={loading} validateRow={validateRow} getValues={getValues} t={t} />,
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
        ],
        [t, rows.length, removeRow, loading, addRow, control, availableRules, validateRow, getValues],
    );

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" PaperComponent={PaperComponent}>
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('timekeeper.employees.dialog.assign_schedule_rule.title')}
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            {alerts && (
                <Box sx={{ top: 0, zIndex: 10, pb: 1, px: 3, pt: 2 }}>
                    <Alert
                        variant="filled"
                        severity="warning"
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>
                                <Button onClick={closeForm} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                </Box>
            )}

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body1" gutterBottom>
                        {data?.full_name}
                    </Typography>

                    <Typography variant="body2" gutterBottom>
                        {t('timekeeper.employees.dialog.assign_schedule_rule.assignments_rules')}
                    </Typography>

                    {loading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            density="standard"
                            disableColumnMenu
                            disableRowSelectionOnClick
                            hideFooter
                            hideFooterPagination
                            hideFooterSelectedRowCount
                            showCellVerticalBorder
                            showColumnVerticalBorder
                            rowHeight={80}
                            autosizeOptions={{ outliersFactor: 0.5 }}
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontSize: '0.875rem',
                                    fontWeight: 'bold',
                                    whiteSpace: 'normal',
                                    lineHeight: 1.2,
                                    textAlign: 'center',
                                    height: 'auto',
                                },
                                '& .MuiDataGrid-row': {
                                    transition: 'all 0.3s ease-in-out',
                                },
                            }}
                        />
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={handleClose} disabled={loading}>
                    {t('general.buttons.close')}
                </Button>
                <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={loading}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
